import { FirebaseDocument } from "common/models/FirebaseDocument";
import BatchService from "common/services/BatchService";
import { LoginStore } from "modules/login/LoginStore";
import { WBSModels } from "modules/WBS/WBSModels";
import { WBSStore } from "modules/WBS/WBSStore";
import { fromFirestoreToWBS, WBS as WBSModel } from "modules/WBS/models/WBS";
import { WBSProjectData as WBSProjectDataModel } from "modules/WBS/models/WBSProjectData";
import { DevelopmentStore } from "modules/development/DevelopmentStore";
import { WBSPropertiesStore } from "modules/WBS/WBSPropertiesStore";
import { default as firebase, firestore } from "common/Firebase";
import { ModelMapService } from "modules/modelMap/ModelMapService";
import { CustomPropertyService } from "modules/customProperties/CustomPropertyService";
import { CustomPropertyStore } from "modules/customProperties/CustomPropertyStore";
import { now } from "utils/FirebaseUtils";
import { ProfileService } from "modules/profile/ProfileService";
import { gtag, install } from "ga-gtag";
import axios from "axios";
import {
  IResourceToUpdate,
  IWBSCalculateRules,
  IWBSItemData,
  IWBSItemOcurrence,
} from "common/interfaces";
import { sum } from "utils/DecimalUtils";
import { env } from "process";

class Service {
  private devsProjectsSnapshot?: () => void;
  private projectSnapshot?: () => void;
  private wbsRowsListener?: () => void;

  getDocRef = (path) =>
    firestore[path.split("/").length % 2 === 0 ? "doc" : ("collection" as any)](
      path
    );

  async createWBS(wbs: WBSModel) {
    const ref = await firestore.collection("wbsProjectBudget").add({ ...wbs });
    return { ref, WBS: new FirebaseDocument<WBSModel>(ref.id, wbs) };
  }

  async selectWbs(id) {
    WBSStore.loadingDataTable = true;
    await firestore
      .collection("wbsProjectBudget")
      .doc(WBSStore.currentWbsId)
      .update({ isSelected: false });
    await firestore
      .collection("wbsProjectBudget")
      .doc(id)
      .update({ isSelected: true });
    WBSStore.setCurrentWbsId(id);
    await WBSStore.getWbsById(DevelopmentStore.developmentId);
    await this.newUpdateWBS(true);
    ModelMapService.verifyCurrentWbsNeedToUpdatedAtAllModelMaps();
    WBSStore.loadingDataTable = false;
  }

  async getWbsName() {
    let snapshot = await firestore
      .collection("wbsProjectBudget")
      .doc(WBSStore.currentWbsId)
      .get();

    if (snapshot.exists) {
      let data = snapshot.data();
      if (data) {
        return data.nameWbs;
      }
    }
  }

  async getWBSRef(devId) {
    let snapshot = await firestore
      .collection("wbsProjectBudget")
      .where("development", "==", devId)
      .limit(1)
      .get();

    if (snapshot.docs[0]) {
      let doc = snapshot.docs[0],
        ref = doc.ref,
        data = doc.data();
      if (data) {
        let {
            name,
            totalCost,
            childrenCount,
            development,
            decimalPlaces,
            roundingRule,
            bdi,
            totalSale,
          } = data,
          docModel = new WBSModel(
            name,
            totalCost,
            childrenCount,
            development,
            decimalPlaces,
            roundingRule,
            bdi,
            totalSale
          ),
          WBS = new FirebaseDocument<WBSModel>(doc.id, docModel),
          rows = await this.getWBSRows(ref),
          customProperties =
            await CustomPropertyService.getAllWBSCustomProperties(ref); //PRUBK-792 - Propriedades personalizadas
        return { ref, WBS, rows, customProperties };
      }
    }
    return null;
  }

  async subscribeWBSToChangeDecimalRules(wbsId) {
    const doc = await firestore.collection("wbsProjectBudget").doc(wbsId);
    // Recupera o arquivo caso necessite atualiza-lo
    // WBSStore.loading = true;
    this.clearProjectSnapshot();
    this.projectSnapshot = doc.onSnapshot((snapshot) => {
      let change = snapshot;
      if (change.id === null) throw new Error("doc.id is null");
      if (change.data) {
        let data = change.data();
        if (data && data.needUpdateWBS) {
          firestore
            .collection("wbsProjectBudget")
            .doc(change.id)
            .update({
              needUpdateWBS: firebase.firestore.FieldValue.delete(),
            })
            .then(() => {
              WBSStore.changeDecimalAndRule();
            });
        }
      }
    });
  }

  clearProjectSnapshot() {
    if (this.projectSnapshot) this.projectSnapshot();
  }

  splitArrayIntoBatches<T>(array: T[], batchSize: number): T[][] {
    let result: T[][] = [];
    for (let i = 0; i < array.length; i += batchSize) {
      result.push(array.slice(i, i + batchSize));
    }
    return result;
  }

  /** Executa o serviço responsável por atualizar os valores dos insumos e
   * suas respectivas composições, de acordo com o dados salvos no banco de composição
   * escolhido, e atualizar a EAP no banco de dados.
   */
  async updateRateLibrariesResourcesOnDataBase(
    wbs: FirebaseDocument<WBSModel>,
    wbsCalculate: IWBSCalculateRules,
    resourcesToUpdate: IResourceToUpdate[]
  ) {
    const batchSize = 50;
    const batches = this.splitArrayIntoBatches(resourcesToUpdate, batchSize);

    for (const batch of batches) {
      const data = {
        WBSId: wbs.id,
        totalCostGlobal: wbs.data.totalCost || 0,
        totalBdiCostGlobal: wbs.data.totalSale || 0,
        wbsCalculate,
        toUpdate: batch,
      };

      await axios.post(`${process.env.REACT_APP_UPDATE_RATE_LIBRARY}`, data);
    }
  }

  removeUndefinedFieldsFromUpdatedRow(lineOld, lineNew) {
    let lineOldCopy = JSON.parse(JSON.stringify(lineOld));
    let lineNewCopy = JSON.parse(JSON.stringify(lineNew));
    for (const item of Object.keys(lineOldCopy.data)) {
      if (lineNewCopy[item] == undefined && item != "tree") {
        lineNewCopy[item] = firebase.firestore.FieldValue.delete();
      }
    }
    return lineNewCopy;
  }

  async updateWBSProjectData(wbsId, projectData: WBSProjectDataModel) {
    let baseMonth = projectData.baseMonth;
    let projectTerm =
      projectData.projectTerm || firebase.firestore.FieldValue.delete();
    let landArea =
      projectData.landArea || firebase.firestore.FieldValue.delete();
    let privateUnits =
      projectData.privateUnits || firebase.firestore.FieldValue.delete();
    let builtArea =
      projectData.builtArea || firebase.firestore.FieldValue.delete();
    let equivalentArea =
      projectData.equivalentArea || firebase.firestore.FieldValue.delete();
    let privateArea =
      projectData.privateArea || firebase.firestore.FieldValue.delete();
    let bdi = projectData.bdi || firebase.firestore.FieldValue.delete();

    await firestore.collection("wbsProjectBudget").doc(wbsId).update({
      baseMonth,
      projectTerm,
      landArea,
      privateUnits,
      builtArea,
      equivalentArea,
      privateArea,
      bdi,
    });
  }

  async getWBSRows(ref) {
    let wbsRowsRef = ref.collection("rows"),
      snapshot = await wbsRowsRef.get(),
      rows = new Array();

    rows = snapshot.docs.map((doc: any) => {
      return WBSModels.wbsRows(doc);
    });

    return rows;
  }

  async newUpdateWBS(fetch, createdWBS?) {
    let devId: string;

    if (createdWBS) {
      devId = createdWBS.data.projectBound;
    } else {
      devId = DevelopmentStore.developmentSelected.id;
    }

    let docWBS: any = await firestore
      .collection("wbsProjectBudget")
      .where("development", "==", DevelopmentStore.developmentId)
      .where("isSelected", "==", true)
      .get()
      .then((snapshot) => snapshot.docs[0]);

    if (!docWBS) {
      docWBS = await firestore
        .collection("wbsProjectBudget")
        .where("development", "==", devId)
        .limit(1)
        .get()
        .then((snapshot) => snapshot.docs[0]);

      if (docWBS) {
        await firestore
          .collection("wbsProjectBudget")
          .doc(docWBS.id)
          .update({
            isSelected: true,
            nameWbs: docWBS.data().nameWbs || "Plano de custo 01",
            createdAt: now(),
          });
      }
    }

    if (!docWBS) throw Error("Document not Found!");
    WBSStore.setCurrentWbsId(docWBS.id);

    const wbsRef = docWBS.ref;
    const rowsRef = wbsRef.collection("rows");
    this.clearWbsRowsSnapshot();
    this.wbsRowsListener = rowsRef
      .orderBy("rowIndex", "asc")
      .onSnapshot(async (snap) => {
        DevelopmentStore.loadingWbsUpdate = false;
        const document = await firestore
          .collection("wbsProjectBudget")
          .where("development", "==", devId)
          .where("isSelected", "==", true)
          .get()
          .then((snapshot) => snapshot.docs[0]);
        if (!document) return;

        const data_ = document.data();
        const model = new WBSModel(
          data_.name,
          data_.totalCost,
          data_.childrenCount,
          data_.development,
          data_.decimalPlaces,
          data_.roundingRule,
          data_.nameWbs,
          data_.description,
          data_.createdAt,
          data_.isSelected
        );

        const WBS = new FirebaseDocument<WBSModel>(document.id, model);
        const rows = snap.docs.map((linha) => WBSModels.wbsRows(linha));

        WBSPropertiesStore.getWBSProjectData(document.id, data_);
        WBSPropertiesStore.wbsCalculate = {
          decimalPlaces: data_.decimalPlaces,
          roundingRule: data_.roundingRule,
        };

        //PRUBK-792 - Propriedades personalizadas
        const customProperties =
          await CustomPropertyService.getAllWBSCustomProperties(wbsRef);
        CustomPropertyStore.setWBSCustomProperties(customProperties);
        WBSStore.getWBS(fetch, createdWBS, {
          ref: wbsRef,
          WBS,
          rows,
          customProperties,
        });
      });

    await WBSStore.setWbsName();
  }

  clearWbsRowsSnapshot() {
    if (this.wbsRowsListener) this.wbsRowsListener();
  }

  // Observar wbsProjectBudget Start
  async projectUpdate() {
    let devId = DevelopmentStore.developmentSelected.id;
    this.clearDevsProjectsSnapshot();
    this.devsProjectsSnapshot = await firestore
      .collection("wbsProjectBudget")
      .where("development", "==", devId)
      .where("isSelected", "==", true)
      .limit(1)
      .onSnapshot((snapshot) => {
        DevelopmentStore.loadingWbsUpdate = false;
        if (snapshot.docs[0]) {
          let doc = snapshot.docs[0];
          let ref = doc.ref,
            data = doc.data();
          let {
              name,
              totalCost,
              childrenCount,
              development,
              decimalPlaces,
              roundingRule,
              bdi,
              totalSale,
              updateCompositions,
            } = data,
            docModel = new WBSModel(
              name,
              totalCost,
              childrenCount,
              development,
              decimalPlaces,
              roundingRule,
              bdi,
              totalSale,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              updateCompositions
            ),
            WBS = new FirebaseDocument<WBSModel>(doc.id, docModel);
          WBSStore.totalCost = WBS.data.totalCost;
          WBSStore.currentWbsTotalCost = WBS.data.totalCost;
          WBSStore.WBS = WBS;
          WBSPropertiesStore.wbsCalculate.decimalPlaces =
            WBSStore.WBS.data.decimalPlaces;
          WBSPropertiesStore.wbsCalculate.roundingRule =
            WBSStore.WBS.data.roundingRule;
        }
      });
  }

  clearDevsProjectsSnapshot() {
    if (this.devsProjectsSnapshot) this.devsProjectsSnapshot();
  }
  // Observar wbsProjectBudget End

  /**
   * Recupera as quantidades totais dos insumos
   */
  async getWBSResourceQuantities(ref) {
    let wbsResRef = ref.collection("resources"),
      snapshot = await wbsResRef.get(),
      res = new Array();
    res = snapshot.docs.map((doc: any) => {
      let data: any = doc.data();
      return { id: doc.id, qtt: data.total };
    });

    return res;
  }

  async filterWBSActivity(activity, description: string) {
    let array = new Array();
    let normalizeSearch = description
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
    let arrayString = normalizeSearch.split(" ");

    //if(ActivitySearchStore.clientInfoTasks.length === 0 || ActivitySearchStore.clientInfoResources.length === 0 ){
    let docs = WBSStore.WBSRows.filter((r) => r.data.rowType == activity);

    for (let row of docs) {
      if (row.data.description) {
        let normalizeDescription = row.data.description
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase();
        let arrayFind = true;
        for (let i = 0; i < arrayString.length; i++) {
          if (normalizeDescription.indexOf(arrayString[i]) < 0) {
            arrayFind = false;
          }
        }
        // Filtra apenas o primeiro que achar com o mesmo contentId
        if (
          arrayFind &&
          array.findIndex((a) => a.data.contentId == row.data.contentId) == -1
        ) {
          array.push(row);
        }
      }
    }

    return array;
  }

  batch(actions) {
    return BatchService.apply(actions);
  }

  async updateWBSSubcollection(
    ref,
    id,
    contentId: string,
    update,
    collection = "rows"
  ) {
    let actions = new Array();
    let updateRows = { ...update };

    for (var key of Object.keys(update)) {
      if (update[key] === undefined) {
        update[key] = firebase.firestore.FieldValue.delete();
      }
    }

    let listContent = WBSStore.getListByContent(contentId, id);

    for (let i = 0; i < listContent.length; i++) {
      let rowRef = this.getRowRef(ref, listContent[i].id);
      actions.push({
        type: "update",
        args: [rowRef, update],
        arrayChange: () => {
          WBSStore.changeAllArray(updateRows, listContent[i]);
        },
      });
    }
    await WBSStore.batchActions(actions, false);
    return true;
  }

  async addInWBSSubcollection(ref, doc, collection = "wbsRows") {
    let docRef = await ref.collection(collection).add(doc),
      document = { id: docRef.id, data: () => doc, ref: docRef };
    return { docRef, model: WBSModels[collection](document) };
  }

  getFirebaseDeletedField() {
    return firebase.firestore.FieldValue.delete();
  }

  getRowRef(ref, id: string) {
    return ref.collection("rows").doc(id);
  }

  async setTotalSales(dif: number, totalSale: number, WBSRef) {
    let updateTotalSales = sum(totalSale, dif);
    await WBSRef.update({ totalSale: updateTotalSales });
  }

  async displayUploadOfUpdateComposition(
    type: string,
    WBSRef,
    totalCompositions?: number
  ) {
    await WBSRef.update({
      updateCompositions: {
        isProcessing: type == "updateCompositions" ? false : true,
        progress: 0,
        type: type,
        totalCompositions: totalCompositions ? totalCompositions : 0,
        count: 0,
      },
    });
  }

  async resetUpdateCompositions(WBSRef, status) {
    if (status) {
      await WBSRef.update({
        updateCompositions: {
          isProcessing: false,
          progress: 0,
          type: status.type ? status.type : "",
          totalCompositions: 0 ? status.totalCompositions : 0,
          count: 0,
        },
      });
    }
  }

  /** Chama API responsável por atualizar as composições da EAP escolhidas
   * com os dados de suas similares no banco de composições.
   * @param {string} costPlanId ID do plano de custo
   * @param {string} rateLibraryId ID do banco de composições
   * @param {boolean} changeDescription Define se as descrições na EAP devem ser alteradas
   * @param {string[]} rowCodesList Lista de códigos das composições escolhidas
   * @param {boolean} useResourceGroups Informa se o banco usa grupos de insumos
   * @param {boolean} useAlternativeCode Informa se o banco usa código alternativo
   */
  async sendToUpdateCompositions(
    costPlanId: string,
    rateLibraryId: string,
    changeDescription: boolean,
    rowCodesList: string[],
    useResourceGroups: boolean = false,
    useAlternativeCode: boolean = false
  ) {
    const data = {
      costPlanId,
      rateLibraryId,
      changeDescription,
      rowCode: rowCodesList,
      useResourceGroups,
      useAlternativeCode,
    };

    WBSStore.loadingTaskChange = true;
    WBSStore.loadingTaskUpload = true;
    this.clearWbsRowsSnapshot();
    axios
      .post(
        // `http://localhost:8000/update-composition`,
        `${process.env.REACT_APP_UPDATE_COMPOSITION_SERVICE_URL}/update-composition`,
        data
      )
      .then(() => {
        WBSStore.loadingTaskChange = true;
        WBSStore.changeDecimalAndRule().then(() => {
          setTimeout(() => {
            WBSStore.loadingTaskChange = false;
            WBSStore.loadingTaskUpload = false;
          }, 100);
        });
      });

    gtag("event", "atualizar_composicoes", {
      atualizar_composicoes: 0,
    });
  }

  /** Chama API responsável por comparar as composições no nível da EAP do
   * plano de custo com suas similares no banco de composições escolhido.
   * @param {string} costPlanId ID do plano de custo
   * @param {string} rateLibraryId ID do banco de composições
   */
  async sendToCompareCompositions(costPlanId: string, rateLibraryId: string) {
    const response = await axios({
      method: "POST",
      url: `${process.env.REACT_APP_UPDATE_COMPOSITION_SERVICE_URL}compare-compositions`,
      data: {
        costPlanId: costPlanId,
        rateLibraryId: rateLibraryId,
      },
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        "X-Requested-With": "XMLHttpRequest",
      },
    });

    if (response && response.data) {
      return {
        compositions: response.data.compositions,
        hasAlternativeCode: Boolean(response.data.hasAlternativeCode),
        hasDescriptionGroup: Boolean(response.data.hasDescriptionGroup),
      };
    }

    return {
      compositions: [],
      hasAlternativeCode: false,
      hasDescriptionGroup: false,
    };
  }

  /** Chama API responsável por adicionar o novo item do banco de composições
   * no plano de custo.
   * @param activity Dados do item no banco de composições
   * @param selectedLine Dados da linha seleciona na EAP ou dentro de composição
   * @param defaultRow Dados iniciais da nova linha a ser inserida
   * @param wbsProjectBudget ID do plano de custo
   */
  async sendToAddCompositionOnEAP(
    activity: any,
    selectedLine: any,
    defaultRow: any,
    wbsProjectBudget: string
  ) {
    let data: any = {
      activity,
      selectedLine,
      defaultRow,
      wbsProjectBudget,
    };

    await axios.post(`${process.env.REACT_APP_ADD_COMPOSITION_EAP}`, {
      data,
    });
  }

  async checkNameWbsAlreadyExists(name: string) {
    let data = await firestore
      .collection("wbsProjectBudget")
      .where("development", "==", DevelopmentStore.developmentId)
      .get();
    if (data) {
      for (let doc of data.docs) {
        if (doc.data().nameWbs === name) {
          return true;
        }
      }
    }
    return false;
  }

  async checkEditNameWbsAlreadyExists(name: string, currentNameWbs: string) {
    let data = await firestore
      .collection("wbsProjectBudget")
      .where("development", "==", DevelopmentStore.developmentId)
      .get();
    if (data) {
      for (let doc of data.docs) {
        if (
          doc.data().nameWbs == name &&
          doc.data().nameWbs !== currentNameWbs
        ) {
          return true;
        }
      }
    }
    return false;
  }

  async unlinkComposition(lineId, newContentId) {
    try {
      let wbsRef = WBSStore.WBSRef;
      let docRef = await wbsRef.collection("rows").doc(lineId);
      await docRef.update({ contentId: newContentId });
      return true;
    } catch (error) {
      console.log("erro ==>>  ", error);
      return false;
    }
  }

  /**
   * Faz a requisição para o serviço responsável por obter as
   * ocorrências do item na EAP.
   * @param {string} wbsId ID do plano de custo.
   * @param {string} contentId ID do conteúdo da linha.
   * @returns {Promise<{ item: IWBSItemData; rows: IWBSItemOcurrence[] }>}
   */
  async getOccurrencesOfItem(
    wbsId: string,
    contentId: string
  ): Promise<{ item: IWBSItemData; rows: IWBSItemOcurrence[] }> {
    const data = {
      wbsId,
      contentId,
    };

    return axios
      .post(`${process.env.REACT_APP_GET_ROWS_OCCURENCES}`, data)
      .then((response) => {
        console.log("response", response.data);
        if (response.data) return response.data.data;
        return { item: undefined, rows: [] };
      })
      .catch((error) => {
        console.error("Error on getOccurrencesOfItem", error);
        return [];
      });
  }
}

let WBSService = new Service();

export { WBSService };



// WEBPACK FOOTER //
// ./src/modules/WBS/WBSService.tsx